import { useCallback, useRef, useMemo, useEffect } from 'react';
import { AppNode, NodeType, NodeStatus, ImageGenerationMode, ScriptData } from '@/types';
import { createEmptyScript, readScript } from '@/types/scriptNode';
import { useNodeStore } from '@/stores/nodeStore';

export interface UseGenerateNodeActionsOptions {
  data: AppNode;
  localPrompt: string;
  onNodeAction: (id: string, prompt?: string) => void;
  onNodeUpdate: (id: string, data: Partial<AppNode['data']>, size?: { width?: number; height?: number }, title?: string) => void;
  onAddNode?: (node: AppNode, position: { x: number; y: number }, options?: { connectToNodeId?: string }) => void;
  onSetLocalPrompt: (value: string) => void;
}

export function useGenerateNodeActions(options: UseGenerateNodeActionsOptions) {
  const { data, localPrompt, onNodeAction, onNodeUpdate, onAddNode, onSetLocalPrompt } = options;
  const onNodeUpdateRef = useRef(onNodeUpdate);
  onNodeUpdateRef.current = onNodeUpdate;

  const commitPrompt = useCallback(() => {
    if (localPrompt !== (data.data.prompt || '')) {
      onNodeUpdateRef.current(data.id, { prompt: localPrompt });
    }
  }, [data.id, data.data.prompt, localPrompt]);

  const handleGenerate = useCallback(() => {
    commitPrompt();
    onNodeAction(data.id, localPrompt);
  }, [data.id, localPrompt, commitPrompt, onNodeAction]);

  const handlePromptChange = useCallback((value: string) => {
    onSetLocalPrompt(value);
  }, [onSetLocalPrompt]);

  const handlePromptSubmit = useCallback(() => {
    commitPrompt();
  }, [commitPrompt]);

  const handleImageModeSwitch = useCallback((targetMode: ImageGenerationMode) => {
    if (data.type !== NodeType.IMAGE_GENERATOR) return;

    const needsInput = targetMode !== 'TXT2IMG';
    const hasInput = data.inputs.length > 0;

    if (needsInput && !hasInput && onAddNode) {
      const newNodeId = `n-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      const uploadPosition = { x: data.x - 480, y: data.y };
      const modeLabel = targetMode === 'IMG2IMG' ? '图生图' : targetMode === 'UPSCALE' ? '图片高清' : '风格迁移';

      const newNode: AppNode = {
        id: newNodeId,
        type: NodeType.MEDIA_UPLOAD,
        x: uploadPosition.x,
        y: uploadPosition.y,
        width: 420,
        title: modeLabel,
        status: NodeStatus.IDLE,
        data: {},
        inputs: [],
      };

      onAddNode(newNode, uploadPosition, { connectToNodeId: data.id });
    }

    // 切回 TXT2IMG 时，自动移除所有"未上传任何媒体"的 MEDIA_UPLOAD 上游节点及其连线
    // 已上传图片/视频/音频的上传节点受 PromptEditor 的 tab 禁用逻辑保护，不会进入此分支
    if (targetMode === 'TXT2IMG' && hasInput) {
      const state = useNodeStore.getState();
      const emptyUploadNodeIds: string[] = [];
      for (const inputId of data.inputs) {
        const n = state.nodeMap.get(inputId);
        if (!n) continue;
        if (n.type !== NodeType.MEDIA_UPLOAD) continue;
        const hasMedia = !!(n.data.image || n.data.videoUri || n.data.audioUri ||
          (n.data.images && n.data.images.length > 0) || (n.data.videoUris && n.data.videoUris.length > 0));
        if (!hasMedia) {
          emptyUploadNodeIds.push(inputId);
        }
      }
      if (emptyUploadNodeIds.length > 0) {
        // 先断开连线（清理 IMAGE_GENERATOR 的 inputs 数组），再删除节点
        for (const uploadId of emptyUploadNodeIds) {
          state.removeConnection(uploadId, data.id);
        }
        state.deleteNodes(emptyUploadNodeIds);
      }
    }

    onNodeUpdateRef.current(data.id, { generationMode: targetMode });
  }, [data.type, data.id, data.x, data.y, data.inputs.length, onAddNode]);

  const handleAddUploadNodeForPanorama = useCallback(() => {
    if (data.type !== NodeType.PANORAMA_GENERATOR || !onAddNode) return;

    const newNodeId = `n-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const uploadPosition = { x: data.x - 480, y: data.y };

    const newNode: AppNode = {
      id: newNodeId,
      type: NodeType.MEDIA_UPLOAD,
      x: uploadPosition.x,
      y: uploadPosition.y,
      width: 420,
      title: '图生全景',
      status: NodeStatus.IDLE,
      data: {},
      inputs: [],
    };

    onAddNode(newNode, uploadPosition, { connectToNodeId: data.id });
    onNodeUpdateRef.current(data.id, { panoramaMode: 'img2panorama' });
  }, [data.type, data.id, data.x, data.y, onAddNode]);

  const handleTryAction = useCallback((actionId: string) => {
    if (data.type === NodeType.IMAGE_GENERATOR) {
      const modeMap: Record<string, ImageGenerationMode> = {
        'img2img': 'IMG2IMG',
        'upscale': 'UPSCALE',
      };
      const targetMode = modeMap[actionId];
      if (targetMode) {
        handleImageModeSwitch(targetMode);
      }
    }

    if (data.type === NodeType.PROMPT_INPUT) {
      if (actionId === 'write_content') {
        // 切换到自己编写内容模式，默认尺寸参考详情页策划师产品卖点节点
        const nextIndex = useNodeStore.getState().getNextTextNodeIndex();
        onNodeUpdateRef.current(data.id, {
          subMode: 'self-write',
          editMode: 'preview',
          textNodeIndex: nextIndex,
          nodeSize: { width: 840, height: 472 },
        }, { width: 840, height: 472 });
      }
      // 其他子模式（txt2video, img2prompt, txt2music）后续扩展
    }

    // 脚本节点三入口（脚本节点开发方案 §4.2）：
    // 剧本生成 → 写入脚本骨架（source 标识来源）+ 左侧创建「剧本」自写内容节点并连线，
    //   用户写完剧本后在脚本卡片点「生成分镜脚本」触发生成（上游文本经 orchestrator 自动收集）；
    // 角色生成 → 写入脚本骨架 + 左侧创建 2 个图片节点（角色 1 / 角色 2）并连线，
    //   用户上传角色图后在脚本卡片点「生成分镜脚本」（上游图片经 CHARACTER 策略收集）；
    // 自己编写 → 写入空脚本直接进入完成态（步骤1「确认镜头」），不触发生成
    if (data.type === NodeType.SCRIPT_NODE) {
      const sourceMap: Record<string, ScriptData['source']> = {
        'script_to_shots': 'SCRIPT',
        'character_to_shots': 'CHARACTER',
        'manual_write': 'MANUAL',
      };
      const source = sourceMap[actionId];
      if (!source) return;
      const existing = readScript((data.data as { script?: unknown }).script);
      // 方案 §5.3：MANUAL 首次进入自动命名「脚本 V{n}」（画布内脚本节点序自增），同步更新节点标题
      let manualNodeTitle: string | undefined;
      if (source === 'MANUAL' && !existing) {
        const count = useNodeStore.getState().nodes.filter(n => n.type === NodeType.SCRIPT_NODE).length;
        manualNodeTitle = `脚本 V${count + 1}`;
      }
      const scriptTitle = existing?.title || manualNodeTitle || data.title || '';
      onNodeUpdateRef.current(data.id, {
        script: createEmptyScript(source, scriptTitle),
      }, { width: 420 * 16 / 9, height: 420 }, manualNodeTitle); // 与 GenerateNode 脚本卡同构：短边 420 + 16:9（v1.4.10）

      // 首次进入剧本/角色入口：左侧创建上游节点并连线（connectToNodeId 由 GenerateNode 包装器
      // 自动 addConnection，目标节点 inputs 同步维护，orchestrator/策略即可收集上游内容）
      if (source !== 'MANUAL' && !existing && onAddNode) {
        // 脚本卡固定规格：短边 420 + 16:9（与 onNodeUpdate 写入的尺寸一致）
        const SCRIPT_CARD_HEIGHT = 420;
        const GAP_X = 80; // 与图片节点图生图上传节点同款横向间距
        const newNodeId = `n-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

        if (source === 'SCRIPT') {
          // 左侧创建「剧本」自写内容节点（与文本节点「尝试→自己编写内容」同构：840×472）
          const TEXT_WIDTH = 840;
          const TEXT_HEIGHT = 472;
          const textX = data.x - TEXT_WIDTH - GAP_X;
          const textY = data.y + (SCRIPT_CARD_HEIGHT - TEXT_HEIGHT) / 2; // 垂直居中于脚本卡
          const textNode: AppNode = {
            id: newNodeId,
            type: NodeType.PROMPT_INPUT,
            x: textX,
            y: textY,
            width: TEXT_WIDTH,
            title: '剧本',
            status: NodeStatus.IDLE,
            data: {
              model: 'grsai-nano-banana-pro', // 与 useNodeOperations 工厂默认一致（自写模式不依赖模型）
              subMode: 'self-write',
              editMode: 'preview',
              textNodeIndex: useNodeStore.getState().getNextTextNodeIndex(),
              nodeSize: { width: TEXT_WIDTH, height: TEXT_HEIGHT },
            },
            inputs: [],
          };
          onAddNode(textNode, { x: textX, y: textY }, { connectToNodeId: data.id });
        } else {
          // 左侧创建 2 个图片上传节点（角色 1 / 角色 2），纵向排列并分别连线到脚本节点
          const UPLOAD_WIDTH = 420;
          const UPLOAD_HEIGHT = 420; // 上传节点默认 1:1
          const GAP_Y = 60;
          const stackHeight = UPLOAD_HEIGHT * 2 + GAP_Y;
          const uploadX = data.x - UPLOAD_WIDTH - GAP_X;
          const uploadTopY = data.y + (SCRIPT_CARD_HEIGHT - stackHeight) / 2; // 整组垂直居中于脚本卡
          const uploadTitles = ['角色 1', '角色 2'];
          uploadTitles.forEach((title, i) => {
            const uploadY = uploadTopY + i * (UPLOAD_HEIGHT + GAP_Y);
            const uploadNode: AppNode = {
              id: `${newNodeId}-${i}`,
              type: NodeType.MEDIA_UPLOAD,
              x: uploadX,
              y: uploadY,
              width: UPLOAD_WIDTH,
              title,
              status: NodeStatus.IDLE,
              data: {},
              inputs: [],
            };
            onAddNode(uploadNode, { x: uploadX, y: uploadY }, { connectToNodeId: data.id });
          });
        }
      }
      // 不再点击入口即触发生成：等用户填写剧本 / 上传角色图后，
      // 由脚本卡片「生成分镜脚本」按钮触发（ScriptContent 待生成态 CTA）
    }
  }, [data.type, data.id, data.x, data.y, (data.data as { script?: unknown }).script, data.title, handleImageModeSwitch, onNodeAction, onAddNode]);

  const currentImageMode = useMemo((): ImageGenerationMode => {
    if (data.type !== NodeType.IMAGE_GENERATOR) return 'TXT2IMG';
    const mode = data.data.generationMode as ImageGenerationMode | undefined;
    const hasInput = data.inputs.length > 0;
    if (mode && ['TXT2IMG', 'IMG2IMG', 'UPSCALE', 'STYLE'].includes(mode)) {
      if (mode !== 'TXT2IMG' && !hasInput) return 'TXT2IMG';
      return mode;
    }
    return hasInput ? 'IMG2IMG' : 'TXT2IMG';
  }, [data.type, data.data.generationMode, data.inputs.length]);

  // 当生成模式需要输入但节点没有输入时，自动切回 TXT2IMG 模式
  useEffect(() => {
    if (data.type !== NodeType.IMAGE_GENERATOR) return;
    const mode = data.data.generationMode as ImageGenerationMode | undefined;
    if (!mode || mode === 'TXT2IMG') return;
    if (data.inputs.length === 0) {
      // 自动副作用：直接写 store，不记历史（避免 undo 后污染历史栈）
      useNodeStore.getState().updateNode(data.id, { generationMode: 'TXT2IMG' });
    }
  }, [data.type, data.data.generationMode, data.inputs.length, data.id]);

  // 当视频模式没有输入时，设置为 txt2video；有输入时设置为 omni
  useEffect(() => {
    if (data.type !== NodeType.VIDEO_GENERATOR) return;
    const currentMode = data.data.generationMode as string | undefined;
    if (data.inputs.length > 0 && currentMode === 'txt2video') {
      // 自动副作用：直接写 store，不记历史
      useNodeStore.getState().updateNode(data.id, { generationMode: 'omni' });
    } else if (data.inputs.length === 0 && currentMode && currentMode !== 'txt2video') {
      // 自动副作用：直接写 store，不记历史
      useNodeStore.getState().updateNode(data.id, { generationMode: 'txt2video' });
    }
  }, [data.type, data.inputs.length, data.id]);

  return {
    onNodeUpdateRef,
    commitPrompt,
    handleGenerate,
    handlePromptChange,
    handlePromptSubmit,
    handleImageModeSwitch,
    handleAddUploadNodeForPanorama,
    handleTryAction,
    currentImageMode,
  };
}
